<template>
  <div v-if="hasReport" class="w-full flex flex-col gap-4">
    <!-- Filters / header panel -->
    <div class="sm:p-8 p-4 bg-blue-100 rounded-md grid grid-cols-1 gap-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="font-medium block">Creative:</label>
          <div class="w-full px-3 py-2 bg-white rounded border text-sm break-words">
            {{ creativeLabel }}
          </div>
        </div>
        <div>
          <label class="font-medium block">Country:</label>
          <div class="w-full px-3 py-2 bg-white rounded border text-sm break-words">
            {{ countryLabel }}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="font-medium block">Start Date:</label>
          <div class="w-full px-3 py-2 bg-white rounded border text-sm">
            {{ startDateLabel }}
          </div>
        </div>
        <div>
          <label class="font-medium block">End Date:</label>
          <div class="w-full px-3 py-2 bg-white rounded border text-sm">
            {{ endDateLabel }}
          </div>
        </div>
      </div>
    </div>

    <!-- Title strip -->
    <div class="p-4 px-8 bg-white rounded-md text-xl font-medium text-gray-700 text-center flex flex-row items-center lg:justify-between justify-center gap-2 flex-wrap">
      <span>Video Impressions Report</span>
      <span class="text-sm text-gray-500">
        ID: {{ report.id }} • {{ generatedAt }}
      </span>
    </div>

    <!-- Meta data cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      <div
        v-for="(kv, idx) in normalizedMeta"
        :key="idx"
        class="bg-white rounded-lg shadow p-4"
      >
        <div class="text-xs text-gray-500">{{ kv.label }}</div>
        <div class="text-base font-medium break-words">{{ kv.value }}</div>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <div class="text-xs text-gray-500">Total Video Impressions (Campaign)</div>
        <div class="text-base font-medium">
          {{ formatInt(report.overview_total_video_impressions) }}
        </div>
      </div>

      <div v-if="report.completes != null" class="bg-white rounded-lg shadow p-4">
        <div class="text-xs text-gray-500">Completes</div>
        <div class="text-base font-medium">
          {{ formatInt(report.completes) }}
        </div>
      </div>

      <div v-if="report.video_length_seconds != null" class="bg-white rounded-lg shadow p-4">
        <div class="text-xs text-gray-500">Video Length</div>
        <div class="text-base font-medium">
          {{ report.video_length_seconds }}s
        </div>
      </div>

      <div v-if="report.unique_impressions != null" class="bg-white rounded-lg shadow p-4">
        <div class="text-xs text-gray-500">Unique Impressions</div>
        <div class="text-base font-medium">
          {{ formatInt(report.unique_impressions) }}
        </div>
      </div>

      <div v-if="report.unique_completes != null" class="bg-white rounded-lg shadow p-4">
        <div class="text-xs text-gray-500">Unique Completes</div>
        <div class="text-base font-medium">
          {{ formatInt(report.unique_completes) }}
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div
      class="bg-white rounded-lg shadow p-4 flex flex-col gap-2 items-center justify-center"
      v-if="uniquesSeries.length"
    >
      <highcharts class="w-full" :options="uniqueChartOptions" />
    </div>

    <div
      class="bg-white rounded-lg shadow p-4 flex flex-col gap-2 items-center justify-center"
      v-if="totalsSeries.length"
    >
      <highcharts class="w-full" :options="totalsChartOptions" />
    </div>

    <!-- Empty state -->
    <div v-if="!uniquesSeries.length && !totalsSeries.length" class="text-sm text-gray-500 text-center">
      No chart data available for this report.
    </div>
  </div>

  <div v-else class="text-sm text-gray-500">No report selected.</div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue'
import { Chart as HighchartsVue } from 'highcharts-vue'

const props = defineProps({
  report: { type: Object, required: true },
  // optional lookup maps if you want friendlier labels without extra queries
  creativesIndex: { type: Object, default: () => ({}) }, // { uuid: 'Name' }
  countriesIndex: { type: Object, default: () => ({}) }, // { uuid: { name, alpha2 } }
})

/* Components */
const highcharts = HighchartsVue

/* Guards */
const hasReport = computed(() => !!props.report && typeof props.report === 'object')

/* Labels */
const creativeLabel = computed(() => {
  const fromMeta = (props.report?.meta_data || []).find(x => x?.label === 'Creative Name')?.value
  return fromMeta || props.creativesIndex[props.report?.creative_uuid] || props.report?.creative_uuid || '—'
})

const countryLabel = computed(() => {
  const name = props.report?.country?.name
  if (!name) return 'All / None'
  return name
})

const startDateLabel = computed(() => props.report?.date_range?.start ?? '—')
const endDateLabel   = computed(() => props.report?.date_range?.end ?? '—')
const generatedAt    = computed(() => props.report?.created_at ?? '—')

/* Meta normalization */
const normalizedMeta = computed(() => {
  const meta = Array.isArray(props.report?.meta_data) ? props.report.meta_data : []
  return meta
    .filter(x => x && x.label != null)
    .map(x => ({ label: x.label, value: x.value ?? '—' }))
})

/* Series extraction */
const uniquesSeries = computed(() => {
  // prefer precomputed uniques_chart_data; else fall back to mapping meta views if available
  const data = Array.isArray(props.report?.uniques_chart_data)
    ? props.report.uniques_chart_data
    : []
  return data.map(p => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }))
})

const totalsSeries = computed(() => {
  const data = Array.isArray(props.report?.totals_chart_data)
    ? props.report.totals_chart_data
    : Array.isArray(props.report?.views_over_time)
      ? props.report.views_over_time.map(r => ({ x: Number(r.percentage) || 0, y: Number(r.user_count) || 0 }))
      : []
  return data.map(p => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }))
})

/* Chart options (match your existing look/feel) */
const uniqueChartOptions = computed(() => ({
  chart: { type: 'line', marginRight: 90 },
  title: { text: uniqueTitle() },
  xAxis: {
    title: { text: uniqueXAxisTitle() },
    labels: { overflow: 'justify' }
  },
  yAxis: {
    title: { text: 'Impressions' },
    labels: {
      overflow: 'justify',
      formatter: function () {
        // use Highcharts' default when < 1M
        return this.value >= 1000000 ? (this.value / 1000000) + 'M' : this.axis.defaultLabelFormatter.call(this)
      }
    }
  },
  tooltip: {
    formatter: function () {
      const xAxisTitle = this.series?.xAxis?.axisTitle?.textStr || 'Value'
      return `${xAxisTitle}: <b>+${this.x}</b><br/>${this.series.name}: <b>${Number(this.y).toLocaleString()}</b>`
    }
  },
  series: [{ name: 'Impressions', data: uniquesSeries.value }],
  credits: { enabled: false },
  exporting: {}
}))

const totalsChartOptions = computed(() => ({
  chart: { type: 'line', marginRight: 90 },
  title: { text: 'Total Impressions Per Percentage Watched' },
  xAxis: {
    title: { text: 'Percentage Watched' },
    labels: { overflow: 'justify' }
  },
  yAxis: {
    title: { text: 'Impressions' },
    labels: {
      overflow: 'justify',
      formatter: function () {
        return this.value >= 1000000 ? (this.value / 1000000) + 'M' : this.axis.defaultLabelFormatter.call(this)
      }
    }
  },
  tooltip: {
    formatter: function () {
      return `Percentage Watched: <b>+${this.x}</b><br/>${this.series.name}: <b>${Number(this.y).toLocaleString()}</b>`
    }
  },
  series: [{ name: 'Impressions', data: totalsSeries.value }],
  credits: { enabled: false },
  exporting: {}
}))

/* Helpers */
const uniqueTitle = () => {
  const len = Number(props.report?.video_length_seconds ?? 0)
  return len > 500 ? 'Unique Impressions Per Interval Watched' : 'Unique Impressions Per Percentage Watched'
}
const uniqueXAxisTitle = () => {
  const len = Number(props.report?.video_length_seconds ?? 0)
  return len > 500 ? 'Seconds' : 'Percentage'
}
const formatInt = (n) => (typeof n === 'number' ? n.toLocaleString() : n ?? '—')
</script>